import type { ImportAnomalyCode, ImportAnomalyPage, ImportSummaryView } from "@gioia365/contracts";
import { messageKeysUnder, type Locale, type MessageKey } from "@gioia365/i18n";
import { formatCount } from "../i18n/format";
import type { Translate } from "../i18n/locale";
import { countryName, SYSTEM_FIELD_LABELS } from "../leads/card/card-fields";

// I gruppi di «Da controllare» della pagina «Importazione da Close» (piano M2 Task 21, proposta 13 del 1° ottobre 2026):
// uno per codice di `IMPORT_ANOMALY_CODES` dei contratti (la lista dell'importatore), con il nome in italiano semplice e
// «Cosa succede» dai cataloghi, in due livelli: «Da guardare» (avvisi) e «Solo da sapere» (informazioni). Qui anche la
// frase «Che cosa» di ogni caso, scritta dal dettaglio che il server manda: soltanto valori dei campi a scelta,
// conteggi e riferimenti tecnici di Close, mai testo libero né dati dei clienti. Un dettaglio che manca o ha una forma
// inattesa dà la frase più generica, mai un errore.

export type AnomalyLevel = "look" | "know";

/**
 * Il livello di ogni codice, nell'ordine del mock-up (`value_too_long` e `lead_not_updatable` non erano nel mock-up). Un
 * codice nuovo dei contratti senza livello qui non passa il typecheck.
 */
export const ANOMALY_LEVELS = {
  no_phone: "look",
  duplicate_phone: "look",
  invalid_phone: "look",
  phone_from_calls: "look",
  phone_from_other_contact: "look",
  city_not_found: "look",
  city_ambiguous: "look",
  unknown_status: "look",
  unmapped_option: "look",
  field_conflict: "look",
  owner_not_linked: "look",
  invalid_email: "look",
  invalid_date: "look",
  missing_in_source: "look",
  local_change_kept: "look",
  source_changed_after_import: "look",
  value_too_long: "look",
  lead_not_updatable: "look",
  unknown_opportunity_status: "look",
  opportunity_not_imported: "look",
  opportunity_incomplete: "look",
  task_not_imported: "look",
  appointment_not_imported: "know",
  saved_search_not_imported: "look",
  joined_by_mobile: "know",
  name_split_uncertain: "know",
  extra_contacts: "know",
  extra_phones: "know",
  city_foreign: "know",
  author_not_linked: "know",
  created_on_used: "know",
  last_call_mismatch: "know",
  activity_not_imported: "know",
  recording_not_copied: "know",
  attachments_not_copied: "know",
  nul_characters_removed: "know",
} satisfies Record<ImportAnomalyCode, AnomalyLevel>;

/** Un gruppo da mostrare: un codice conosciuto, oppure uno che la pagina non conosce ancora (nel livello degli avvisi). */
export type AnomalyGroup = { code: string; level: AnomalyLevel; known: boolean; count: number };

const KNOWN_CODES = Object.keys(ANOMALY_LEVELS) as ImportAnomalyCode[];

function isKnown(code: string): code is ImportAnomalyCode {
  return Object.hasOwn(ANOMALY_LEVELS, code);
}

/** I gruppi del rapporto: tutti quelli conosciuti (anche a zero, per «Nessun caso»), poi gli sconosciuti con dei casi. */
export function anomalyGroups(counts: Readonly<Record<string, number>>): AnomalyGroup[] {
  const known = KNOWN_CODES.map((code): AnomalyGroup => ({ code, level: ANOMALY_LEVELS[code], known: true, count: counts[code] ?? 0 }));
  const unknown = Object.entries(counts)
    .filter(([code, count]) => !isKnown(code) && count > 0)
    .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
    .map(([code, count]): AnomalyGroup => ({ code, level: "look", known: false, count }));
  return [...known, ...unknown];
}

export function anomalyName(t: Translate, group: Pick<AnomalyGroup, "code">): string {
  return isKnown(group.code) ? t(`closeImport.anomalies.${group.code}.name`) : t("closeImport.anomalies.unknown.name", { code: group.code });
}

export function anomalyWhat(t: Translate, group: Pick<AnomalyGroup, "code">): string {
  return isKnown(group.code) ? t(`closeImport.anomalies.${group.code}.what`) : t("closeImport.anomalies.unknown.what");
}

/** Ciò che serve a scrivere «Che cosa»: i nomi degli utenti di Close e dei campi, presi dal riepilogo dell'esecuzione. */
export type DetailContext = { t: Translate; locale: Locale; summary: ImportSummaryView };

type Detail = ImportAnomalyPage["items"][number]["detail"];

const text = (detail: Detail, key: string): string | null => {
  const value = detail[key];
  return typeof value === "string" && value.trim() !== "" ? value : null;
};

const count = (detail: Detail, key: string): number | null => {
  const value = detail[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
};

const LEAD_CARD_FIELDS = new Set<string>(messageKeysUnder("leadCard.fields"));

/** La chiave di un gruppo del catalogo, se esiste davvero: un valore sconosciuto del server non diventa un errore. */
function keyUnder(group: string, name: string | null): MessageKey | null {
  if (name === null) return null;
  const keys: readonly string[] = messageKeysUnder(group);
  const key = `${group}.${name}`;
  return keys.includes(key) ? (key as MessageKey) : null;
}

/** Il nome di un utente di Close dal riepilogo; il suo identificativo tecnico se il riepilogo non lo nomina. */
export function closeUserName(summary: ImportSummaryView, closeUserId: string | null): string | null {
  if (closeUserId === null) return null;
  return summary.users.find((user) => user.closeUserId === closeUserId)?.closeName ?? closeUserId;
}

/** Il nome di un campo dell'azienda (`field:<chiave>` o la sola chiave) come il piano lo ha salvato nel riepilogo. */
function companyFieldLabel(summary: ImportSummaryView, fieldKey: string): string {
  const ref = fieldKey.startsWith("field:") ? fieldKey : `field:${fieldKey}`;
  return summary.variables.find((variable) => variable.destination === ref && variable.destinationLabel)?.destinationLabel ?? ref.slice("field:".length);
}

/** Il nome di un campo del Lead da un riferimento (`system:first_name`, `field:fiche_media`) o da una colonna (`salonName`). */
export function fieldName(ctx: DetailContext, field: string): string {
  const system = SYSTEM_FIELD_LABELS[field];
  if (system) return ctx.t(system);
  if (field.startsWith("field:")) return companyFieldLabel(ctx.summary, field);
  // Gli altri campi di sistema (`system:status`, `system:second_holder.email`) e le colonne del Lead (`salonName`): la
  // stessa etichetta della scheda, se c'è.
  const camel = field.replace(/^system:/, "").replace(/[._]([a-z])/g, (_, letter: string) => letter.toUpperCase());
  const key = `leadCard.fields.${camel}`;
  return LEAD_CARD_FIELDS.has(key) ? ctx.t(key as MessageKey) : field;
}

function holderOf(ctx: DetailContext, detail: Detail): string {
  return ctx.t(text(detail, "holder") === "second" ? "closeImport.detail.holderSecond" : "closeImport.detail.holderPrimary");
}

function phoneSlot(ctx: DetailContext, detail: Detail): string {
  const slot = `${text(detail, "holder") ?? ""}:${text(detail, "kind") ?? ""}`;
  if (slot === "primary:mobile") return ctx.t("closeImport.detail.phoneSlot.primaryMobile");
  if (slot === "primary:landline") return ctx.t("closeImport.detail.phoneSlot.primaryLandline");
  if (slot === "second:mobile") return ctx.t("closeImport.detail.phoneSlot.secondMobile");
  return ctx.t("closeImport.detail.phoneSlot.other");
}

function withReason(sentence: string, reason: string | null): string {
  return reason ? `${sentence} · ${reason}` : sentence;
}

/** «Che cosa» di un caso, in parole semplici; una stringa vuota quando il dettaglio non ha niente da dire. */
export function describeAnomaly(ctx: DetailContext, item: Pick<ImportAnomalyPage["items"][number], "code" | "detail">): string {
  const { t, locale, summary } = ctx;
  const { detail } = item;
  const n = (key: string) => formatCount(locale, count(detail, key) ?? 0);
  const keyed = (group: string, key: string) => {
    const found = keyUnder(group, text(detail, key));
    return found ? t(found) : null;
  };
  switch (item.code) {
    case "no_phone":
      return t("closeImport.detail.noPhone", { calls: n("calls"), others: n("otherContactPhones") });
    case "phone_from_calls":
      return t("closeImport.detail.phoneFromCalls", { calls: n("calls") });
    case "phone_from_other_contact": {
      const contact = count(detail, "contact");
      if (contact === 2) return t("closeImport.detail.phoneFromSecond");
      return contact === null ? "" : t("closeImport.detail.phoneFromContact", { contact: formatCount(locale, contact) });
    }
    case "invalid_phone":
      return t("closeImport.detail.invalidPhone", {
        holder: holderOf(ctx, detail),
        reason: keyed("closeImport.detail.phoneReason", "reason") ?? t("closeImport.detail.phoneReason.other"),
      });
    case "duplicate_phone": {
      const parts = [t("closeImport.detail.duplicateClose", { phone: phoneSlot(ctx, detail), count: n("closeLeads") })];
      if ((count(detail, "gioiaLeads") ?? 0) > 0) parts.push(t("closeImport.detail.duplicateGioia", { count: n("gioiaLeads") }));
      return parts.join("; ");
    }
    case "joined_by_mobile":
      return t(detail.reason === "archived" ? "closeImport.detail.joinedArchived" : "closeImport.detail.joined");
    case "extra_contacts":
      return t("closeImport.detail.extraContacts", { count: n("count") });
    case "extra_phones":
      return t("closeImport.detail.extraPhones", { count: n("count") });
    case "attachments_not_copied":
      return t("closeImport.detail.attachments", { count: n("count") });
    case "city_not_found": {
      const province = text(detail, "province");
      return province ? t("closeImport.detail.provinceWritten", { province }) : t("closeImport.detail.provinceEmpty");
    }
    case "city_ambiguous":
      return t("closeImport.detail.candidates", { count: n("candidates") });
    case "city_foreign": {
      const country = text(detail, "country");
      return country
        ? t("closeImport.detail.country", { country: countryName(locale, country.toUpperCase(), t("closeImport.detail.italy")) })
        : t("closeImport.detail.countryUnknown");
    }
    case "unknown_status": {
      const status = text(detail, "closeStatus");
      return status ? t("closeImport.detail.closeStatus", { status }) : t("closeImport.detail.closeStatusUnknown");
    }
    case "unmapped_option": {
      const value = text(detail, "value");
      const fieldKey = text(detail, "fieldKey");
      const reason = keyed("closeImport.detail.optionReason", "reason");
      if (fieldKey === "call_outcome") return withReason(value ? t("closeImport.detail.callOutcome", { value }) : "", reason);
      const field = text(detail, "closeLabel") ?? (fieldKey ? companyFieldLabel(summary, fieldKey) : null);
      if (field === null) return reason ?? "";
      return withReason(value ? t("closeImport.detail.option", { field, value }) : t("closeImport.detail.optionNoValue", { field }), reason);
    }
    case "field_conflict": {
      const field = text(detail, "closeLabel");
      const fieldKey = text(detail, "fieldKey");
      if (field === null) return "";
      return fieldKey ? t("closeImport.detail.conflict", { field, target: companyFieldLabel(summary, fieldKey) }) : t("closeImport.detail.conflictNoTarget", { field });
    }
    case "owner_not_linked": {
      const name = closeUserName(summary, text(detail, "closeUserId"));
      const reason = keyed("closeImport.detail.ownerReason", "reason");
      return name ? withReason(t("closeImport.detail.ownerName", { name }), reason) : t("closeImport.detail.ownerUnknown");
    }
    case "author_not_linked":
      return t("closeImport.detail.author", {
        name: closeUserName(summary, text(detail, "closeUserId")) ?? t("closeImport.detail.authorUnknown"),
        count: n("activities"),
      });
    case "name_split_uncertain": {
      const words = count(detail, "words");
      if (words === 1) return t("closeImport.detail.nameOneWord", { holder: holderOf(ctx, detail) });
      return t("closeImport.detail.nameWords", { holder: holderOf(ctx, detail), count: n("words") });
    }
    case "invalid_email":
      return t("closeImport.detail.emailOf", { holder: holderOf(ctx, detail) });
    case "invalid_date": {
      const field = text(detail, "field");
      const type = text(detail, "closeType");
      if (field === "activity_at") return type ? `${t("closeImport.detail.dateActivity")} · ${t("closeImport.detail.closeType", { type })}` : t("closeImport.detail.dateActivity");
      if (field === "date_created") return t("closeImport.detail.dateCreated");
      return field ? t("closeImport.detail.dateField", { field }) : "";
    }
    case "created_on_used":
      return t("closeImport.detail.createdOn");
    case "last_call_mismatch":
      return keyed("closeImport.detail.lastCall", "reason") ?? "";
    case "missing_in_source":
      return t("closeImport.detail.missing");
    case "local_change_kept": {
      const field = text(detail, "field");
      if (field === "opportunity") return withReason(t("closeImport.detail.keptOpportunity"), keyed("closeImport.detail.keptReason", "reason"));
      if (field === "task") return withReason(t("closeImport.detail.keptTask"), keyed("closeImport.detail.keptReason", "reason"));
      if (field === null) return "";
      return withReason(t("closeImport.detail.kept", { field: fieldName(ctx, field) }), keyed("closeImport.detail.keptReason", "reason"));
    }
    case "source_changed_after_import": {
      const reason = text(detail, "reason");
      if (reason === "mapping_changed" || reason === "not_in_source") return t(`closeImport.detail.changed.${reason}`);
      if (reason === "source_changed") return t("closeImport.detail.changed.note");
      const entity = text(detail, "entity");
      return t(entity === "call" || entity === "note" ? `closeImport.detail.changed.${entity}` : "closeImport.detail.changed.other");
    }
    case "activity_not_imported": {
      // Email, conversazioni email e SMS non sono tipi sconosciuti: non fanno parte di Gioia365.
      const outOfScope = keyed("closeImport.detail.outOfScope", "closeType");
      if (outOfScope) return outOfScope;
      const type = text(detail, "closeType");
      return type ? t("closeImport.detail.closeType", { type }) : t("closeImport.detail.closeTypeUnknown");
    }
    case "recording_not_copied": {
      const recording = detail.recording === true;
      const voicemail = detail.voicemail === true;
      if (recording && voicemail) return t("closeImport.detail.recordingAndVoicemail");
      if (voicemail) return t("closeImport.detail.voicemail");
      return t("closeImport.detail.recording");
    }
    case "nul_characters_removed":
      return t("closeImport.detail.nul", { count: n("values") });
    case "value_too_long": {
      const field = text(detail, "field");
      return field ? t("closeImport.detail.tooLong", { field: fieldName(ctx, field), length: n("length"), max: n("max") }) : "";
    }
    case "lead_not_updatable":
      return keyed("closeImport.detail.notUpdatable", "reason") ?? "";
    case "unknown_opportunity_status": {
      const stage = text(detail, "closeStatus");
      return stage ? t("closeImport.detail.opportunityStage", { stage }) : t("closeImport.detail.opportunityStageUnknown");
    }
    case "opportunity_not_imported":
      return keyed("closeImport.detail.opportunityReason", "reason") ?? "";
    case "opportunity_incomplete":
      return keyed("closeImport.detail.opportunityMissing", "reason") ?? "";
    case "saved_search_not_imported":
      return keyed("closeImport.handover.savedSearchReason", "reason") ?? "";
    case "appointment_not_imported": {
      const reason = keyed("closeImport.detail.appointmentReason", "reason") ?? "";
      const kind = keyed("closeImport.detail.appointmentKind", "kind");
      return reason !== "" && kind ? `${kind} · ${reason}` : reason;
    }
    case "task_not_imported": {
      const reason = keyed("closeImport.detail.taskReason", "reason") ?? "";
      const type = text(detail, "closeType");
      return reason !== "" && type ? `${reason} · ${t("closeImport.detail.closeType", { type })}` : reason;
    }
    default:
      return "";
  }
}

/** Il riferimento di Close di un caso: la risorsa in parole e l'identificativo tecnico. */
export function anomalyReference(t: Translate, item: Pick<ImportAnomalyPage["items"][number], "resource" | "externalId">): string {
  const id = item.externalId;
  if (item.resource === "lead") return t("closeImport.check.refs.lead", { id });
  if (item.resource === "activity") return t("closeImport.check.refs.activity", { id });
  if (item.resource === "user") return t("closeImport.check.refs.user", { id });
  if (item.resource === "opportunity") return t("closeImport.check.refs.opportunity", { id });
  if (item.resource === "task") return t("closeImport.check.refs.task", { id });
  if (item.resource === "appointment") return t("closeImport.check.refs.appointment", { id });
  if (item.resource === "saved_search") return t("closeImport.check.refs.saved_search", { id });
  return t("closeImport.check.refs.other", { resource: item.resource, id });
}
